import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Card, CardContent, Table, TableHead, TableRow, TableCell,
    TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem, Alert, Chip, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import api from '../services/api';

const formatCurrency = (v) => `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const defaultItem = { description: '', itemType: 'material', quantity: 1, unit: 'UN', notes: '' };

const ListDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [item, setItem] = useState(defaultItem);
    const [error, setError] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);

    const fetchList = useCallback(() => {
        api.get(`/lists/${id}`).then(r => setList(r.data.data)).finally(() => setLoading(false));
    }, [id]);
    useEffect(() => { fetchList(); }, [fetchList]);

    const setField = (field) => (e) => setItem(prev => ({ ...prev, [field]: e.target.value }));

    const handleAddItem = async () => {
        if (!item.description.trim() || !item.quantity || item.quantity <= 0) {
            setError('Preencha descrição e quantidade'); return;
        }
        await api.post(`/lists/${id}/items`, item);
        setOpenDialog(false);
        setItem(defaultItem);
        setError('');
        fetchList();
    };

    const handleRemoveQuote = async (itemId, quoteId) => {
        if (!window.confirm('Remover esta cotação?')) return;
        await api.delete(`/lists/${id}/items/${itemId}/quotes/${quoteId}`);
        fetchList();
    };

    const handleRemoveItem = async (itemId) => {
        if (!window.confirm('Remover item?')) return;
        await api.delete(`/lists/${id}/items/${itemId}`);
        fetchList();
    };

    const handleExportPDF = async () => {
        try {
            const response = await api.get(`/reports/list/${id}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `lista-${list.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            alert('Erro ao gerar relatório PDF. Verifique se a lista possui itens.');
        }
    };

    if (loading) return <Box sx={{ py: 6, textAlign: 'center' }}><Typography>Carregando...</Typography></Box>;
    if (!list) return <Alert severity="error">Lista não encontrada</Alert>;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <IconButton onClick={() => navigate('/listas')}><ArrowBackIcon /></IconButton>
                <Typography variant="h5" fontWeight={700}>{list.name}</Typography>
                {/*<Chip label={list.status === 'finalized' ? 'Finalizada' : 'Rascunho'} size="small" color={list.status === 'finalized' ? 'success' : 'default'} />*/}
            </Box>
            {list.description && <Typography color="text.secondary" mb={2} ml={6}>{list.description}</Typography>}

            <Box sx={{ display: 'flex', gap: 2, mb: 3, ml: 6 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>Adicionar Item</Button>
                <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF}>Exportar PDF</Button>
            </Box>

            {/* Summary */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Card sx={{ flex: 1, minWidth: 180 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Total de itens</Typography>
                        <Typography variant="h4" fontWeight={700} color="primary">{list.items?.length || 0}</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 2, minWidth: 240, bgcolor: '#074960' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>Valor Total Estimado</Typography>
                        <Typography variant="h4" fontWeight={700} color="white">{formatCurrency(list.totalEstimated)}</Typography>
                    </CardContent>
                </Card>
            </Box>

            <Card>
                <CardContent>
                    <Typography variant="h6" fontWeight={600} mb={2}>Itens da Lista</Typography>
                    {(!list.items || list.items.length === 0) ? (
                        <Alert severity="info">Nenhum item adicionado. Clique em "Adicionar Item" para começar.</Alert>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell align="center">Qtd</TableCell>
                                    <TableCell>Unidade</TableCell>
                                    <TableCell align="right">Preço Unit.</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="center">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {list.items.map((it, i) => (
                                    <React.Fragment key={it._id}>
                                        <TableRow hover>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>{it.description}</Typography>
                                                {it.notes && <Typography variant="caption" color="text.secondary">{it.notes}</Typography>}
                                            </TableCell>
                                            <TableCell><Chip label={it.itemType === 'service' ? 'Serviço' : 'Material'} size="small" variant="outlined" /></TableCell>
                                            <TableCell align="center">{it.quantity}</TableCell>
                                            <TableCell>{it.unit}</TableCell>
                                            <TableCell align="right">
                                                <Typography color="primary" fontWeight={600}>
                                                    {formatCurrency(it.estimatedPrice)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ({it.quotes?.length || 0} cotações)
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right"><Typography fontWeight={700}>{formatCurrency(it.estimatedPrice * it.quantity)}</Typography></TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                    <Tooltip title="Adicionar Cotações">
                                                        <IconButton size="small" color="primary" onClick={() => navigate(`/pesquisa?listId=${id}&itemId=${it._id}&query=${it.description}&itemType=${it.itemType}`)}>
                                                            <AddShoppingCartIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Ver Cotações">
                                                        <IconButton size="small" onClick={() => setItem(prev => ({ ...prev, expandedId: prev.expandedId === it._id ? null : it._id }))}>
                                                            {item.expandedId === it._id ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Remover item">
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveItem(it._id)}><DeleteIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                        {item.expandedId === it._id && (
                                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                                <TableCell colSpan={8}>
                                                    <Box sx={{ p: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight={700} mb={1}>Cotações Selecionadas</Typography>
                                                        {(!it.quotes || it.quotes.length === 0) ? (
                                                            <Typography variant="body2" color="text.secondary">Nenhuma cotação adicionada ainda.</Typography>
                                                        ) : (
                                                            <Table size="small" sx={{ bgcolor: 'white' }}>
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Fonte</TableCell>
                                                                        <TableCell>Órgão / Empresa</TableCell>
                                                                        <TableCell>Data</TableCell>
                                                                        <TableCell align="right">Preço</TableCell>
                                                                        <TableCell align="center">Ações</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {it.quotes.map((q, j) => (
                                                                        <TableRow key={j}>
                                                                            <TableCell><Chip label={q.source} size="small" /></TableCell>
                                                                            <TableCell>
                                                                                <Typography variant="body2">{q.agency || q.supplier}</Typography>
                                                                                <Typography variant="caption" color="text.secondary">{q.region}</Typography>
                                                                            </TableCell>
                                                                            <TableCell>{q.date ? new Date(q.date).toLocaleDateString('pt-BR') : '-'}</TableCell>
                                                                            <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(q.price)}</TableCell>
                                                                            <TableCell align="center">
                                                                                <IconButton size="small" color="error" onClick={() => handleRemoveQuote(it._id, q._id)}>
                                                                                    <DeleteIcon fontSize="mini" />
                                                                                </IconButton>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {list.items?.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 2, borderTop: '2px solid #074960' }}>
                            <Typography variant="h6" fontWeight={700} color="primary">Total: {formatCurrency(list.totalEstimated)}</Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Add Item Dialog */}
            <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setItem(defaultItem); setError(''); }} maxWidth="sm" fullWidth>
                <DialogTitle>Adicionar Item</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Descrição do item" value={item.description} onChange={setField('description')} fullWidth required />
                        <FormControl fullWidth>
                            <InputLabel>Tipo</InputLabel>
                            <Select value={item.itemType} onChange={setField('itemType')} label="Tipo">
                                <MenuItem value="material">Material</MenuItem>
                                <MenuItem value="service">Serviço</MenuItem>
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Quantidade" type="number" value={item.quantity} onChange={setField('quantity')} inputProps={{ min: 0.001, step: 0.001 }} sx={{ flex: 1 }} />
                            <TextField label="Unidade" value={item.unit} onChange={setField('unit')} sx={{ flex: 1 }} />
                        </Box>
                        <TextField label="Observações (opcional)" value={item.notes} onChange={setField('notes')} multiline rows={2} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenDialog(false); setItem(defaultItem); setError(''); }}>Cancelar</Button>
                    <Button variant="contained" onClick={handleAddItem}>Adicionar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ListDetailPage;
